'use client';

import { useState, useEffect } from 'react';
import { MermaidDiagram } from './MermaidDiagram';

export function HomePanel() {
  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* What is this */}
        <section className="bg-secondary-background border-2 border-border rounded-base p-6 shadow-shadow">
          <h3 className="text-lg font-heading text-foreground mb-3">What is this?</h3>
          <div className="space-y-3 text-foreground/80">
            <p>
              For the last month I've been working on a long-running coding agent harness.
              This demo serves to show off some OSS projects I've built using it.
            </p>
            <ul className="space-y-2 text-foreground/80">
              <li className="flex items-start gap-2">
                <span className="text-main font-bold">→</span>
                <span className="flex-1">
                  <a className="text-main underline font-semibold" href="https://github.com/doda/looper" target="_blank" rel="noreferrer">
                    Looper
                  </a>{" "}
                  - a long-running coding agent harness that runs continuously with memory, tests, and tooling
                  <span className="block text-xs text-foreground/60 font-base mt-1">TypeScript, ~6k LOC</span>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-main font-bold">→</span>
                <span className="flex-1">
                  <a className="text-main underline font-semibold" href="https://github.com/doda/dray" target="_blank" rel="noreferrer">
                    Dray
                  </a>{" "}
                  - a leaderless, object-storage based Kafka broker implemented in Golang, that compacts
                  records to Iceberg/Parquet (inspired by WarpStream/Ursa Engine)
                  <span className="block text-xs text-foreground/60 font-base mt-1">Go, ~134k LOC</span>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-main font-bold">→</span>
                <span className="flex-1">
                  <a className="text-main underline font-semibold" href="https://github.com/doda/vex" target="_blank" rel="noreferrer">
                    Vex
                  </a>{" "}
                  - an object-storage based vector search engine (inspired by turbopuffer)
                  <span className="block text-xs text-foreground/60 font-base mt-1">Go, ~124k LOC</span>
                </span>
              </li>
            </ul>
            <p>
              The motivation behind this was to explore the current capabilities of frontier models
              to implement complex, well-defined infrastructure projects.
              Needless to say, all of these are experimental software.
            </p>
          </div>
        </section>

        {/* Architecture */}
        <section className="bg-secondary-background border-2 border-border rounded-base p-6 shadow-shadow">
          <h3 className="text-lg font-heading text-foreground mb-3">How it works</h3>
          <MermaidDiagram
            maxWidth={350}
            chart={`flowchart TD
    A[Bluesky Firehose] -->|WebSocket| B[firehose-ingest]
    B -->|Posts| C[Dray]
    C -->|Kafka| F[Vex]
    C -->|Compaction| E[Iceberg on S3]
    F -->|Search| H[api-gateway]
    E -->|Analytics via DuckDB| H
    H --> I[Frontend]

    style A fill:#88aaee,stroke:#000,stroke-width:2px
    style B fill:#88aaee,stroke:#000,stroke-width:2px
    style C fill:#a3e635,stroke:#000,stroke-width:2px
    style F fill:#a3e635,stroke:#000,stroke-width:2px
    style E fill:#fef08a,stroke:#000,stroke-width:2px
    style H fill:#88aaee,stroke:#000,stroke-width:2px
    style I fill:#88aaee,stroke:#000,stroke-width:2px`}
          />
          <div className="flex gap-4 mt-3 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-[#a3e635] border-2 border-border rounded-sm" />
              <span className="text-foreground/70">OSS</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-[#88aaee] border-2 border-border rounded-sm" />
              <span className="text-foreground/70">Services</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-[#fef08a] border-2 border-border rounded-sm" />
              <span className="text-foreground/70">Storage</span>
            </div>
          </div>
          <ul className="space-y-2 text-foreground/80 mt-4">
            <li className="flex items-start gap-2">
              <span className="text-main font-bold">→</span>
              <span><strong>Dray:</strong> Kafka-compatible broker that compacts messages into Iceberg tables</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-main font-bold">→</span>
              <span><strong>Vex:</strong> Vector + full-text search engine for semantic queries</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-main font-bold">→</span>
              <span><strong>api-gateway:</strong> Queries Vex for search, uses embedded DuckDB for analytics on Iceberg</span>
            </li>
          </ul>
        </section>

        {/* Why it's cool */}
        <section className="bg-secondary-background border-2 border-border rounded-base p-6 shadow-shadow">
          <h3 className="text-lg font-heading text-foreground mb-3">Why it's cool</h3>
          <ul className="space-y-2 text-foreground/80">
            <li className="flex items-start gap-2">
              <span className="text-main font-bold">→</span>
              <span>
                <strong>Real-time stream → search:</strong> Bluesky posts flow from firehose ingest
                into Dray and Vex, so search updates almost immediately.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-main font-bold">→</span>
              <span>
                <strong>Hybrid retrieval:</strong> Vector similarity and BM25 text search run together,
                letting you query by intent or exact phrasing.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-main font-bold">→</span>
              <span>
                <strong>Object‑storage first:</strong> Dray compacts to Iceberg/Parquet and Vex persists
                indexes in object storage, keeping the stack durable and replayable.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-main font-bold">→</span>
              <span>
                <strong>Search + analytics in sync:</strong> DuckDB queries the same Iceberg tables,
                so dashboards and search stay aligned.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-main font-bold">→</span>
              <span>
                <strong>Purpose-built OSS:</strong> Looper, Dray, and Vex are all original projects,
                wired together end‑to‑end in this demo.
              </span>
            </li>
          </ul>
        </section>

        {/* Tech stack */}
        <section className="bg-secondary-background border-2 border-border rounded-base p-6 shadow-shadow">
          <h3 className="text-lg font-heading text-foreground mb-3">Tech Stack</h3>
          <div className="flex flex-wrap gap-2">
            {[
              'Go',
              'Kafka (Dray)',
              'Vex',
              'DuckDB',
              'Next.js',
              'TypeScript',
              'AT Protocol',
            ].map((tech) => (
              <span
                key={tech}
                className="bg-main text-main-foreground px-3 py-1 rounded-base border-2 border-border text-sm font-base"
              >
                {tech}
              </span>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
}
